import { z } from 'zod';
/**
 * The ten stages of the v3.0 Operations Process Flow. This is the spine of the whole
 * system — every job sits at exactly one stage, and the stage determines what may
 * happen next.
 */
export const JOB_STAGES = [
    { n: 1, key: 'ENTRY', title: 'Entry point', detail: 'Customer discovers Handy Nest' },
    { n: 2, key: 'INTAKE', title: 'Booking', detail: 'Job captured over WhatsApp' },
    {
        n: 3,
        key: 'RECORDED',
        title: 'Job record + alerts',
        detail: 'Record created, notifications sent',
    },
    {
        n: 4,
        key: 'MATCHED',
        title: 'Matched to artisan',
        detail: 'Best-fit artisan contacted, 30 min to reply',
    },
    { n: 5, key: 'QUOTED', title: 'Quote sent', detail: 'Awaiting explicit customer YES' },
    {
        n: 6,
        key: 'PAYMENT',
        title: 'Payment via Yoco',
        detail: 'Full job amount into the Handy Nest account',
    },
    {
        n: 7,
        key: 'RELEASED',
        title: 'Contacts released',
        detail: 'Both ways, only after payment clears',
    },
    {
        n: 8,
        key: 'IN_PROGRESS',
        title: 'Work in progress',
        detail: 'Artisan and customer coordinate directly',
    },
    {
        n: 9,
        key: 'COMPLETION',
        title: 'Completion + rating',
        detail: 'Photos sent, customer confirms and rates',
    },
    {
        n: 10,
        key: 'PAID_OUT',
        title: 'Friday EFT payout',
        detail: '85% to artisan, 15% commission retained',
    },
];
/**
 * Job status — finer grained than stage, because a job can sit in several distinct
 * states within one stage (e.g. QUOTED and AWAITING_PAYMENT are both stage 5-6 territory).
 */
export const jobStatusSchema = z.enum([
    'NEW', // captured, not yet matched
    'MATCHED', // offered to an artisan, awaiting their reply
    'DECLINED', // artisan declined; awaiting rematch
    'QUOTED', // quote with the customer, awaiting explicit YES
    'ACCEPTED', // customer said YES, payment link sent
    'PAID', // Yoco confirmed — contacts still locked until released
    'ACTIVE', // contacts released both ways, work under way
    'COMPLETED', // completion photos sent, awaiting customer sign-off
    'CONFIRMED', // customer signed off and rated, queued for payout
    'CLOSED', // EFT paid
    'CANCELLED', // customer said no, or job abandoned
    'EXCEPTION', // out of area, dispute, or other off-path handling
]);
/** Which stage each status corresponds to, for the progress tracker in the portal. */
export const STATUS_STAGE = {
    NEW: 3,
    MATCHED: 4,
    DECLINED: 4,
    QUOTED: 5,
    ACCEPTED: 5,
    PAID: 6,
    ACTIVE: 8,
    COMPLETED: 9,
    CONFIRMED: 9,
    CLOSED: 10,
    CANCELLED: 3,
    EXCEPTION: 3,
};
/**
 * Statuses in which the contact-sharing barrier is OPEN.
 *
 * This list is deliberately short and deliberately explicit. A job is only ever in one
 * of these states as the result of a verified Yoco payment webhook — see
 * `apps/api/src/domain/job/release-contacts.ts`.
 */
export const CONTACTS_RELEASED_STATUSES = [
    'ACTIVE',
    'COMPLETED',
    'CONFIRMED',
    'CLOSED',
];
export const contactsAreReleased = (status) => CONTACTS_RELEASED_STATUSES.includes(status);
export const urgencySchema = z.enum(['EMERGENCY', 'SAME_DAY', 'WITHIN_3_DAYS', 'WITHIN_A_WEEK']);
export const URGENCY_LABEL = {
    EMERGENCY: 'Emergency',
    SAME_DAY: 'Same day',
    WITHIN_3_DAYS: 'Within 3 days',
    WITHIN_A_WEEK: 'Within a week',
};
export const tradeSchema = z.enum([
    'PLUMBING',
    'ELECTRICAL',
    'PAINTING',
    'CARPENTRY',
    'APPLIANCE_REPAIR',
    'LOCKSMITH',
    'GARDENING',
    'HANDYMAN',
]);
export const TRADE_LABEL = {
    PLUMBING: 'Plumbing & Geysers',
    ELECTRICAL: 'Electrical',
    PAINTING: 'Painting & Waterproofing',
    CARPENTRY: 'Carpentry',
    APPLIANCE_REPAIR: 'Appliance Repair',
    LOCKSMITH: 'Locksmith',
    GARDENING: 'Gardening & Landscaping',
    HANDYMAN: 'General Handyman',
};
/** Job reference as shown to customers and artisans: HN-1042. */
export const jobRefSchema = z.string().regex(/^HN-\d{4,}$/, 'Job reference must look like HN-1042');
//# sourceMappingURL=job.js.map